import type { ReactNode } from 'react'

/**
 * Section opener. Three entrance treatments share one component so spacing
 * and type scale stay consistent across sections:
 *
 *   1. eyebrow → H2 → sub   (Hero-style entrance — used once, on Hero only)
 *   2. rule    → H2 → sub   (40px brand-colour hairline; editorial margin note)
 *   3.          H2 → sub   (no preamble — the H2 carries the section alone)
 *
 * Choosing a different opener per section is intentional: when every chapter
 * arrives the same way, the page reads as one long scroll.
 */
export default function SectionHeader({
  eyebrow,
  heading,
  sub,
  align = 'start',
  light = false,
  rule = false,
}: {
  eyebrow?: string
  heading: ReactNode
  sub?: ReactNode
  align?: 'start' | 'center'
  light?: boolean
  rule?: boolean
}) {
  const alignCls = align === 'center' ? 'text-center items-center' : 'text-start items-start'
  return (
    <div className={`flex flex-col ${alignCls} max-w-3xl ${align === 'center' ? 'mx-auto' : ''}`}>
      {eyebrow ? (
        <p className={`mb-5 text-[11px] uppercase tracking-[0.22em] font-medium ${light ? 'text-surface/90' : 'text-accent'}`}>
          {eyebrow}
        </p>
      ) : rule ? (
        <span
          aria-hidden
          className={`mb-6 block h-px w-10 ${light ? 'bg-surface/55' : 'bg-accent'}`}
        />
      ) : null}
      <h2
        className={`font-display-serif text-3xl sm:text-4xl md:text-5xl leading-[1.1] ${light ? 'text-surface' : 'text-ink'}`}
        style={{ fontVariationSettings: '"opsz" 48, "wght" 600' }}
      >
        {heading}
      </h2>
      {sub && (
        <p className={`mt-4 text-base leading-relaxed max-w-2xl ${light ? 'text-surface/85' : 'text-ink-muted'}`}>
          {sub}
        </p>
      )}
    </div>
  )
}
